import React from 'react';
import _ from 'underscore';

import AaSequenceAlignmentModal from 'analyses/aa-sequence-alignment/AaSequenceAlignmentModal';
import specifierForAaSequence from 'analyses/aa-sequence-alignment/util/specifierForAaSequence';
import DataConnector from 'connect/DataConnector';
import {ProteinAlignment} from 'constants/AlignmentConstants';
import AaSequenceAlignmentStore from 'molbio/aa-sequences/AaSequenceAlignmentStore';
import AaSequenceStore from 'molbio/aa-sequences/AaSequenceStore';
import {AaSequenceAlignmentSummary} from 'molbio/aa-sequences/util/AaSequenceConstants';
import getTestProps from 'test/lib/cypress/getTestProps';
import AaSequenceAlignmentPanelTable from 'toolbar/aa-sequence/AaSequenceAlignmentPanelTable';

interface AaSequenceAlignmentPanelProps {
  proteinId: string;
  closePanel: () => void;
}

interface EnhancedProps {
  alignments: Array<ProteinAlignment | AaSequenceAlignmentSummary>;
  linkedAlignments: Array<ProteinAlignment | AaSequenceAlignmentSummary>;
}

class AaSequenceAlignmentPanelConnector extends DataConnector<AaSequenceAlignmentPanelProps, EnhancedProps> {
  override shouldSubscribeToRedux = false;
  override stores = [AaSequenceAlignmentStore];

  _sortByCreatedAt(alignments: Array<ProteinAlignment>): Array<ProteinAlignment> {
    return _.sortBy(alignments, 'createdAt').reverse();
  }

  enhanceProps(): EnhancedProps {
    return {
      alignments: this._sortByCreatedAt(this.aaSequenceAlignmentStore().getAlignments(this.props.proteinId)),
      linkedAlignments: this._sortByCreatedAt(
        this.aaSequenceAlignmentStore().getLinkedAlignments(this.props.proteinId)
      ),
    };
  }
}

class AaSequenceAlignmentPanel extends React.Component<AaSequenceAlignmentPanelProps & EnhancedProps> {
  _allowFeatureEdits = (): boolean =>
    AaSequenceStore.getAaSequenceById(this.props.proteinId)?.permissions.polymerFeaturesEditable ?? false;

  _onCreateAlignment = (): void => {
    const aaSequence = AaSequenceStore.getAaSequenceById(this.props.proteinId)!;
    AaSequenceAlignmentModal.openWithTemplateSpecifier(specifierForAaSequence(aaSequence));
  };

  _renderAlignments = (): JSX.Element => (
    <div>
      <h5 className="u-spacing-top">Saved Alignments</h5>
      {this.props.alignments.length ? (
        <AaSequenceAlignmentPanelTable
          alignments={this.props.alignments}
          showActionButtons={this._allowFeatureEdits()}
          closePanel={this.props.closePanel}
          dataTestAttrs={{
            component: 'ProteinAlignmentPanel',
            element: 'proteinAlignmentTable',
          }}
        />
      ) : (
        <div className="u-small-spacing-top u-small-spacing-bottom">No saved AA sequence alignments.</div>
      )}
    </div>
  );

  _renderLinkedAlignments = (): JSX.Element => (
    <div>
      <h5 className="u-spacing-top">Linked Alignments</h5>
      {this.props.linkedAlignments.length ? (
        <AaSequenceAlignmentPanelTable
          alignments={this.props.linkedAlignments}
          showActionButtons={false}
          closePanel={this.props.closePanel}
        />
      ) : (
        <div className="u-small-spacing-top u-small-spacing-bottom">No linked AA sequence alignments.</div>
      )}
    </div>
  );

  override render(): JSX.Element {
    return (
      <div {...getTestProps({component: 'ProteinAlignmentPanel', element: 'root'})}>
        <button
          className="btn btn-af-primary btn-block u-small-spacing-top"
          onClick={this._onCreateAlignment}
          disabled={!this._allowFeatureEdits()}
          type="button"
        >
          Create New Alignment
        </button>
        {this._renderAlignments()}
        {this._renderLinkedAlignments()}
      </div>
    );
  }
}

export default AaSequenceAlignmentPanelConnector.connect(AaSequenceAlignmentPanel);
